import React from 'react'
import { Map, Set, List } from 'immutable'
import _map from 'lodash/map'
import _size from 'lodash/size'
import _isArray from 'lodash/isArray'
import {
  TYPE_CONTENT_PRODUCT,
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_PERSON,
  TYPE_CONTENT_TAG,
  TYPE_CONTENT_SERIES_GUIDE,
  TYPE_CONTENT_EPISODE_GUIDE,
  isGuideType,
  TYPE_CONTENT_COURSE,
  TYPE_CONTENT_ARTICLE,
} from '../../services/content-type/index'
import { COURSE_STATE_IN_PROGRESS, COURSE_STATE_NOT_STARTED, COURSE_STATE_COMPLETE } from '../../services/detail/index'
import { URL_GAIA_PLUS } from '../../services/url/constants'
import {
  formatSeriesEpisodeTotals,
  formatDuration,
} from '../../theme/web-app/index'
import { isFeatureAllowedWithSubscription, featureIsFree } from '../../services/subscription/index'
import Link from '../Link/index'
import { TARGET_BLANK } from '../Link/constants'
import Vote from '../Vote/index'
import WatchV2 from '../Watch.v2/index'
import { getWatchAccess } from '../../services/media/index'
import PlaylistAddRemove from '../PlaylistAddRemove/index'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'
import PlayOutlineIcon from '../Icon/icons/PlayOutline/index'
import PlusIcon from '../Icon/icons/Plus/index'
import XIcon from '../Icon/icons/X/index'
import LockedIcon from '../Icon/icons/Locked/index'
import ArrowRightIcon from '../Icon/icons/ArrowRight/index'
import Button, { TYPES } from '../Button/index'
import TextSeasonEpisode from '../TextSeasonEpisode/index'
import { isEpisode, getPrimaryCourse, isSubtypeYogaFitnessMeditation } from '../../services/content/index'
import TileV2Tags from './Tile.v2Tags'
import TileV2Guests from './Tile.v2Guests'
import {
  getTileUrl,
  handleTileV2OnClick,
  getTileV2ElementClassName,
  isLiveAccessUpgradeToWatch,
  handleRemoveFromContinueWatching,
} from './utils'

/**
 * The title rendered as a link
 * @param {Object} props - The props
 * @param {String} props.title - The content title
 * @param {String} props.url - The url of the content
 * @param {Function} props.onClick - The onclick function
 * @param {String} props.contentType - The type of content
 * @returns {JSX.Element|null} - The content title as a link
 */
function TitleLink (props) {
  const {
    title,
    url,
    onClick,
    contentType,
  } = props
  const isContentTypeProduct = contentType === TYPE_CONTENT_PRODUCT

  if (!title || !url) {
    return null
  }

  return (
    <Link
      to={url}
      className={`tile-v2__title tile-v2__title--${contentType}`}
      onClick={onClick}
      target={isContentTypeProduct ? TARGET_BLANK : null}
      directLink={isContentTypeProduct}
    >
      {title}
    </Link>
  )
}

/**
 * Renders the season and episode numbers
 * @param {Object} props - The props
 * @param {Number} props.seasonNumber - The season number
 * @param {Number} props.episodeNumber - The episode number
 * @returns {String|null} - If seasonNumber and episodeNumber are null, return null.
 *  Otherwise return the formatted season and episode numbers
 */
function SeasonEpisodeNumbers (props) {
  const { seasonNumber, episodeNumber, isGuide } = props

  return (
    <TextSeasonEpisode
      className={['tile-v2__season-episode-numbers']}
      season={seasonNumber}
      episode={episodeNumber}
      isGuide={isGuide}
    />
  )
}

/**
 * Series season episode in the "Series Title: S1:E1" format
 * @param {Object} props - The props
 * @param {String} props.title - The content title
 * @param {Number} props.seasonNumber - The season number
 * @param {Number} props.episodeNumber - The episode number
 * @returns {JSX.Element|null} Series title combined with the season and episode number
 */
function SeriesSeasonEpisode (props) {
  const {
    title,
    url,
    seasonNumber,
    episodeNumber,
    isGuide,
    staticText,
  } = props

  if (!title || !seasonNumber || !episodeNumber) {
    return null
  }

  return (
    <div className="tile-v2__series-season-episode">
      <SeasonEpisodeNumbers
        seasonNumber={seasonNumber}
        episodeNumber={episodeNumber}
        isGuide={isGuide}
      />
      <span className="tile-v2__series-season-episode-separator">
        {` ${staticText.get('of')} `}
      </span>
      <Link
        className="tile-v2__series-title-link"
        to={url}
        onClick={(e) => handleTileV2OnClick(e, props, false, 'Tile Series Title')}
      >
        {title}
      </Link>
    </div>
  )
}

/**
 * Upgrade to watch button for Gaia+
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Component for upgrade to watch CTA
 */
function UpgradeToWatchButton (props) {
  const { staticText, subscriptions } = props
  const hasSubscription = List.isList(subscriptions) && subscriptions.size > 0
  return (
    <Link
      className="tile-v2__upgrade-btn"
      to={URL_GAIA_PLUS}
    >
      <span className="tile-v2__upgrade-icon">
        <LockedIcon size={20} />
      </span>
      <span className="tile-v2__upgrade-text">
        { hasSubscription ? staticText.get('upgradeWatch') : staticText.get('getGaiaPlusToWatch') }
      </span>
    </Link>
  )
}

/**
 * Feature not allowed with subscription button
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Component for feature not allowed with subscription CTA
 */
function FeatureNotAllowedButton (props) {
  const { staticText, path } = props

  if (!path) {
    return null
  }

  return (
    <Link
      className="tile-v2__feature-not-allowed"
      to={path}
    >
      <span className="tile-v2__feature-not-allowed-icon">
        <PlayOutlineIcon size={20} />
      </span>
      <span className="tile-v2__feature-not-allowed-text">
        {staticText.get('watchNow')}
      </span>
    </Link>
  )
}

/**
 * Metadata controls such as the watch button, add to playlist, etc.
 * @param {Object} props - The props
 * @param {JSX.Element} props.watch - The jsx elements for the watch button
 * @param {JSX.Element} props.playlist - The jsx elements for the add to playlist button
 * @returns {JSX.Element} The metadata action controls
 */
function MetadataControls (props) {
  const { watch, playlist, isContinueWatchingV3 } = props

  return (
    <div className="tile-v2__metadata-controls">
      <div className="tile-v2__watch">
        {watch}
      </div>
      <div className="tile-v2__control-buttons">
        {playlist}
        {isContinueWatchingV3 && (
          <Button
            className="tile-v2__control-button tile-v2__control-button--remove-cw-v3"
            onClick={() => { handleRemoveFromContinueWatching(props) }}
            type={TYPES.ICON}
            iconSvg={<XIcon size={24} />}
          />
        )}
      </div>
    </div>
  )
}

/**
 * The series controls, such as play button, add to playlist, info, etc.
 * @param {Object} props - The props
 * @param {Map} props.upstreamContext - The upstream context
 * @param {Map} props.userNodeInfo - The userNodeInfo for the series, including featured episode
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isLoggedIn - Logged in status for the user
 * @param {Map} props.auth - The user's auth
 * @param {Map} props.data - The data for the series tile
 * @param {Boolean} props.legacyData - Whether or not the data source is coming from a
 * @param {List} props.subscriptions - The user's subscriptions - entitlements
 * @param {String} props.country - The user's country Iso2 code
 *  legacy source (i.e., non member home tile requests)
 * @returns {JSX.Element} - The rendered controls for a series tile
 */
function SeriesControls (props) {
  const {
    upstreamContext = Map(),
    userNodeInfo = Map(),
    staticText = Map(),
    isLoggedIn,
    auth = Map(),
    data = Map(),
    legacyData,
    subscriptions,
    country,
    rowPlacement = null,
    index = null,
    adminTitle,
  } = props
  let featuredEpisode

  if (legacyData) {
    featuredEpisode = Map.isMap(data.get('featuredEpisode')) ? data.get('featuredEpisode', Map()) : Map()
  } else {
    featuredEpisode = Map.isMap(userNodeInfo.get('featuredEpisode')) ? userNodeInfo.get('featuredEpisode', Map()) : Map()
  }

  const featuredEpisodeTitle = featuredEpisode.getIn(['data', 'title'], '')
  const featuredEpisodeId = featuredEpisode.getIn(['data', 'contentId'], -1)
  const featuredEpisodeFeature = featuredEpisode.getIn(['data', 'feature'], Map())
  const featuredEpisodePath = featuredEpisode.getIn(['data', 'path'], '') || data.get('url')
  const watchAccess = getWatchAccess({
    userSubscriptions: subscriptions,
    media: featuredEpisodeFeature,
    country,
  })
  const upgradeToWatch = isLiveAccessUpgradeToWatch({
    feature: featuredEpisodeFeature,
    subscriptions,
  })
  const isFeatureAllowed = isFeatureAllowedWithSubscription(featuredEpisodeFeature, auth)
  const isFreeAllowed = featureIsFree(featuredEpisodeFeature.getIn(['offerings', 'availability']))
  const courseState = data.getIn(['courseProgress', 'state'], null)
  let watchText = staticText.get('watchNow')

  // Set the watch text based on the course state
  if (courseState === COURSE_STATE_IN_PROGRESS) {
    watchText = staticText.get('continueCourse')
  } else if (courseState === COURSE_STATE_NOT_STARTED || courseState === COURSE_STATE_COMPLETE) {
    watchText = staticText.get('startCourse')
  }

  let seriesWatch = null

  if (upgradeToWatch) {
    seriesWatch = (
      <UpgradeToWatchButton {...props} />
    )
  }

  if (!isFeatureAllowed && !upgradeToWatch) {
    seriesWatch = (
      <FeatureNotAllowedButton staticText={staticText} path={featuredEpisodePath} />
    )
  }

  if (watchAccess && isFeatureAllowed && !upgradeToWatch) {
    seriesWatch = (
      <WatchV2
        iconSvg={<PlayOutlineIcon size={20} />}
        upstreamContext={upstreamContext}
        onClickWatch={(e) => handleTileV2OnClick(e, props, true)}
        buttonType={TYPES.ICON}
        className="tile-v2__watch-button"
        text={watchText}
        url={featuredEpisodePath}
        watchAccess={watchAccess}
        isFree={isFreeAllowed}
      />
    )
  }

  const seriesAddPlaylist = (
    <>
      {
        featuredEpisodeId !== -1 && isLoggedIn
          ? <div className="tile-v2__playlist-add-remove" title={staticText.get('addRemove')}>
              <PlaylistAddRemove
                upstreamContext={upstreamContext}
                contentId={featuredEpisodeId}
                contentTitle={featuredEpisodeTitle}
                buttonSvgIcon={<PlusIcon size={24} />}
                notext
                ga4EventData={{
                  content_id: featuredEpisodeId,
                  content_type: featuredEpisode.getIn(['type', 'content']),
                  content_name: featuredEpisodeTitle,
                  media_id: featuredEpisodeFeature.get('id'),
                  media_language: data.get('mediaLanguage'),
                  is_featured_media: true,
                  parent_series_name: data.get('title'),
                  parent_series_id: data.get('id'),
                  ui_element: 'Tile',
                  row_placement: rowPlacement,
                  tile_number: index,
                  row_name: adminTitle,
                }}
              />
            </div>
          : null
      }
    </>
  )

  return (
    <MetadataControls
      {...props}
      watch={seriesWatch}
      playlist={seriesAddPlaylist}
    />
  )
}

/**
 * The video and episode controls, such as play button, add to playlist, info, etc.
 * @param {Object} props - The props
 * @param {Map} props.upstreamContext - The upstream context
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isLoggedIn - Logged in status for the user
 * @param {Map} props.auth - The user's auth
 * @param {Map} props.data - The data for the video or episode tile
 * @param {List} props.subscriptions - The user's subscriptions - entitlements
 * @param {String} props.country - The user's country Iso2 code
 * @returns {JSX.Element} - The rendered controls for a video or episode tile
 */
function VideoEpisodeControls (props) {
  const {
    upstreamContext = Map(),
    staticText = Map(),
    isLoggedIn,
    auth = Map(),
    data = Map(),
    country,
    subscriptions,
    rowPlacement = null,
    index = null,
    adminTitle,
  } = props
  const videoEpisodeTitle = data.get('title', '')
  const videoEpisodeId = data.get('id', -1)
  const videoEpisodeFeature = data.get('feature', Map())
  const videoEpisodeWatchText = staticText.get('watchNow')
  const videoEpisodePath = data.get('url', '')
  const videoMediaIsForbidden = data.getIn(['userInfo', 'media', 'isForbidden'], false) || false
  const watchAccess = getWatchAccess({
    userSubscriptions: subscriptions,
    media: videoEpisodeFeature,
    country,
  })
  const liveAccessUpgradeToWatch = isLiveAccessUpgradeToWatch({
    feature: videoEpisodeFeature,
    subscriptions,
  })
  const upgradeToWatch = liveAccessUpgradeToWatch && videoMediaIsForbidden
  const isFeatureAllowed = isFeatureAllowedWithSubscription(videoEpisodeFeature, auth)
  const isFreeAllowed = featureIsFree(videoEpisodeFeature.getIn(['offerings', 'availability']))
  let videoEpisodeWatch = null

  if (upgradeToWatch) {
    videoEpisodeWatch = (
      <UpgradeToWatchButton {...props} />
    )
  }

  if (!isFeatureAllowed && !upgradeToWatch) {
    videoEpisodeWatch = (
      <FeatureNotAllowedButton staticText={staticText} path={videoEpisodePath} />
    )
  }

  if (watchAccess && isFeatureAllowed && !upgradeToWatch) {
    videoEpisodeWatch = (
      <WatchV2
        iconSvg={<PlayOutlineIcon size={20} />}
        upstreamContext={upstreamContext}
        onClickWatch={(e) => handleTileV2OnClick(e, props, true)}
        buttonType={TYPES.ICON}
        className="tile-v2__watch-button"
        text={videoEpisodeWatchText}
        url={videoEpisodePath}
        watchAccess={watchAccess}
        isFree={isFreeAllowed}
      />
    )
  }

  const videoEpisodeAddPlaylist = (
    <>
      {
        isLoggedIn
          ? <div className="tile-v2__playlist-add-remove" title={staticText.get('addRemove')}>
              <PlaylistAddRemove
                upstreamContext={upstreamContext}
                contentId={videoEpisodeId}
                contentTitle={videoEpisodeTitle}
                buttonSvgIcon={<PlusIcon size={24} />}
                notext
                ga4EventData={{
                  content_id: videoEpisodeId,
                  content_type: data.get('contentType'),
                  content_name: videoEpisodeTitle,
                  media_id: data.getIn(['feature', 'id']),
                  media_language: data.get('mediaLanguage'),
                  is_featured_media: true,
                  parent_series_name: data.get('seriesTitle'),
                  parent_series_id: data.get('seriesId'),
                  ui_element: 'Tile',
                  row_placement: rowPlacement,
                  tile_number: index,
                  row_name: adminTitle,
                }}
              />
            </div>
          : null
      }
    </>
  )

  return (
    <MetadataControls
      {...props}
      watch={videoEpisodeWatch}
      playlist={videoEpisodeAddPlaylist}
    />
  )
}

/**
 * Audio and subtitle icons
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Map} props.userNodeInfo - The userNodeInfo for the content
 * @param {List} props.userLanguages - List of the user's languages
 * @param {Boolean} props.legacyData - Whether or not the data source is coming from a
 *  legacy source (i.e., non member home tile requests)
 * @param {String} props.contentType - The type of content
 * @returns {JSX.Element|null} The audio and subtitle icons
 */
function AudioSubtitleIcons (props) {
  const {
    data = Map(),
    userNodeInfo = Map(),
    userLanguages,
    legacyData,
    contentType,
  } = props
  let availableLangs
  let availableSubtitles
  let availableCaptions
  const mediaLang = data.get('primaryLanguage')
  const langIconsNeeded = !userLanguages.includes(mediaLang)

  if (contentType !== TYPE_CONTENT_SERIES && contentType !== TYPE_CONTENT_VIDEO) {
    return null
  }

  if (contentType === TYPE_CONTENT_SERIES && !legacyData) {
    availableLangs = userNodeInfo.getIn(['featuredEpisode', 'media', 'byLang'], Map()).keySeq().toList()
    availableSubtitles = userNodeInfo.getIn(['featuredEpisode', 'media', 'textTracks', 'subtitles'], Map()).keySeq().toList()
    availableCaptions = userNodeInfo.getIn(['featuredEpisode', 'media', 'textTracks', 'captions'], Map()).keySeq().toList()
  }

  if (contentType === TYPE_CONTENT_SERIES && legacyData) {
    const featuredEpisode = Map.isMap(data.get('featuredEpisode')) ? data.get('featuredEpisode', Map()) : Map()
    availableLangs = featuredEpisode.getIn(['media', 'byLang'], Map()).keySeq().toList()
    availableSubtitles = featuredEpisode.getIn(['media', 'textTracks', 'subtitles'], Map()).keySeq().toList()
    availableCaptions = featuredEpisode.getIn(['media', 'textTracks', 'captions'], Map()).keySeq().toList()
  }

  if (contentType === TYPE_CONTENT_VIDEO && !legacyData) {
    availableLangs = userNodeInfo.getIn(['data', 'media', 'byLang'], Map()).keySeq().toList()
    availableSubtitles = userNodeInfo.getIn(['data', 'media', 'textTracks', 'subtitles'], Map()).keySeq().toList()
    availableCaptions = userNodeInfo.getIn(['data', 'media', 'textTracks', 'captions'], Map()).keySeq().toList()
  }

  if (contentType === TYPE_CONTENT_VIDEO && legacyData) {
    const userInfo = Map.isMap(data.get('userInfo')) ? data.get('userInfo', Map()) : Map()
    const userInfoMedia = Map.isMap(userInfo.get('media')) ? userInfo.get('media', Map()) : Map()
    availableLangs = userInfoMedia.get('byLang', Map()).keySeq().toList()
    availableSubtitles = userInfoMedia.getIn(['textTracks', 'subtitles'], Map()).keySeq().toList()
    availableCaptions = userInfoMedia.getIn(['textTracks', 'captions'], Map()).keySeq().toList()
  }

  // if falsy, bail
  if (!availableLangs || !availableCaptions || !availableSubtitles) {
    return null
  }

  // if we don't have the proper data, bail
  if (
    (List.isList(availableLangs) && availableLangs.size === 0)
      || (List.isList(availableCaptions) && availableCaptions.size === 0
        && List.isList(availableSubtitles) && availableSubtitles.size === 0)
  ) {
    return null
  }

  const availableTextTracks = Set([
    ...availableSubtitles,
    ...availableCaptions,
  ])
  const audioOfInterest = langIconsNeeded
    ? availableLangs.some((al) => userLanguages.includes(al))
    : undefined
  const textTrackOfInterest = langIconsNeeded
    ? availableTextTracks.some((att) => userLanguages.includes(att))
    : undefined
  const icons = []

  if (audioOfInterest) {
    icons.push(<IconV2 type={ICON_TYPES.HEADPHONES} key={'headphones'} />)
  }

  if (textTrackOfInterest) {
    icons.push(<IconV2 type={ICON_TYPES.SWITCH_LANGUAGE_OUTLINE} key={'switch-language'} />)
  }

  return icons.length ? icons : null
}

/**
 * The vote/thumbs up component
 * @param {Object} props - The props
 * @param {Map} - The content data
 * @param {Map} - The user's auth
 * @param {Map} - The featured episode
 * @param {Boolean} - If the featured episode is in progress or not
 * @returns {JSX.Element} - The vote/thumbs up component
 */
function VoteReadOnly (props) {
  const {
    data = Map(),
    auth = Map(),
    featuredEpisode = Map(),
    featuredEpisodeInProgress = false,
  } = props
  const vote = data.get('vote', null)
  const voteDown = data.get('voteDown', null)
  const featuredEpisodeVote = Map.isMap(featuredEpisode) ? featuredEpisode.getIn(['data', 'voteUpCount'], 0) : 0
  const featuredEpisodeDownVote = Map.isMap(featuredEpisode) ? featuredEpisode.getIn(['data', 'voteDownCount'], 0) : 0
  const featuredEpisodeId = Map.isMap(featuredEpisode) ? featuredEpisode.getIn(['data', 'contentId'], -1) : -1
  const id = data.get('id')

  return (
    <Vote
      className="tile-v2__vote-read-only"
      voteDown={featuredEpisodeInProgress ? featuredEpisodeDownVote : voteDown}
      vote={featuredEpisodeInProgress ? featuredEpisodeVote : vote}
      voteId={featuredEpisodeInProgress ? featuredEpisodeId : id}
      auth={auth}
      upOnly
      showLabels
    />
  )
}

function MetadataHeader (props) {
  const {
    title,
    url,
    contentType,
    featuredEpisode = Map(),
    featuredEpisodeInProgress = false,
    data,
    staticText,
  } = props
  const featuredEpisodeDuration = featuredEpisode.getIn(['data', 'feature', 'duration'], 0)
  const duration = data.getIn(['feature', 'duration'], 0)
  const seasonTotal = data.get('seasonCount')
  const seasonText = staticText.get('season')
  const seasonsText = staticText.get('seasons')
  const lessonsTotal = data.get('episodeCount')
  const lessonText = staticText.get('lesson')
  const lessonsText = staticText.get('lessons')
  const isGuide = isGuideType(data)
  const courseStepsTotal = data.getIn(['courseProgress', 'metrics', 'totalModules'], 0)
  const courseStepText = staticText.get('step')
  const courseStepsText = staticText.get('steps')
  let total = 0
  let singularText = ''
  let pluralText = ''

  if (isGuide) {
    total = lessonsTotal
    singularText = lessonText
    pluralText = lessonsText
  } else if (contentType === TYPE_CONTENT_SERIES) {
    total = seasonTotal
    singularText = seasonText
    pluralText = seasonsText
  } else if (contentType === TYPE_CONTENT_COURSE) {
    total = courseStepsTotal
    singularText = courseStepText
    pluralText = courseStepsText
  }

  return (
    <div className="tile-v2__metadata-header">
      <TitleLink
        title={featuredEpisodeInProgress ? featuredEpisode.getIn(['data', 'title'], '') : title}
        url={featuredEpisodeInProgress ? featuredEpisode.getIn(['data', 'path'], '') : url}
        onClick={(e) => handleTileV2OnClick(e, props, false, 'Tile Title')}
        contentType={contentType}
      />
      <div className="tile-v2__metadata-counts">
        {
          featuredEpisodeInProgress || duration
            ? formatDuration(featuredEpisodeDuration || duration)
            : formatSeriesEpisodeTotals(total, singularText, pluralText)
        }
      </div>
    </div>
  )
}

/**
 * Metadata footer, the very bottom of a tile
 * @param {Object} props - The props
 * @param {Array} props.children - The child elements of the component
 * @returns {JSX.Element|null} Wrapped metadata footer elements
 */
function MetadataFooter (props) {
  const { children } = props

  if (!children || !_isArray(children)) {
    return null
  }

  const childrenLength = _size(children)

  return (
    <div className="tile-v2__metadata-footer">
      <div className="tile-v2__metadata-footer-left">
        {
          _map(children, (item, index) => {
            const itemKey = `metadata-item-${index}`

            return (
              <span
                key={itemKey}
                className={`tile-v2__metadata-item${index === childrenLength - 1 ? ' tile-v2__metadata-item--last' : ''}`}
              >
                {item}
              </span>
            )
          })
        }
      </div>
    </div>
  )
}

function SeriesMiddleMetadata (props) {
  const {
    title,
    url,
    featuredEpisodeInProgress = false,
    featuredEpisodeNumber = 0,
    featuredEpisodeSeasonNumber = 0,
    isGuide = false,
    isYogaFitnessMeditation = false,
    staticText,
  } = props
  const middleMetadata = []

  if (!featuredEpisodeInProgress || isYogaFitnessMeditation) {
    middleMetadata.push(<TileV2Tags {...props} key="tags" />)
  }

  if (featuredEpisodeInProgress) {
    middleMetadata.push(
      <SeriesSeasonEpisode
        {...props}
        key="series-season-episode"
        title={title}
        url={url}
        seasonNumber={featuredEpisodeSeasonNumber}
        episodeNumber={featuredEpisodeNumber}
        isGuide={isGuide}
        staticText={staticText}
      />,
    )
  }

  return middleMetadata.length ? middleMetadata : null
}

function VideoEpisodeMiddleMetadata (props) {
  const {
    title,
    url,
    seasonNumber,
    episodeNumber,
    isGuide,
    isContentEpisode,
    isYogaFitnessMeditation,
    staticText,
  } = props
  const middleMetadata = []

  if (!isContentEpisode || isYogaFitnessMeditation) {
    middleMetadata.push(<TileV2Tags {...props} key="tags" />)
  }

  if (isContentEpisode && seasonNumber && episodeNumber) {
    middleMetadata.push(
      <SeriesSeasonEpisode
        {...props}
        key="series-season-episode"
        title={title}
        url={url}
        seasonNumber={seasonNumber}
        episodeNumber={episodeNumber}
        isGuide={isGuide}
        staticText={staticText}
      />,
    )
  }

  return middleMetadata.length ? middleMetadata : null
}

function CourseControls (props) {
  const {
    staticText = Map(),
    data = Map(),
    country,
    subscriptions,
  } = props
  const courseState = data.getIn(['courseProgress', 'state'], null)
  const courseFeature = data.get('feature', Map())
  const url = getTileUrl(props)
  const watchAccess = getWatchAccess({
    userSubscriptions: subscriptions,
    media: courseFeature,
    country,
  })

  if (!watchAccess || !url) {
    return null
  }

  return courseState === COURSE_STATE_IN_PROGRESS
    ? <Button
      type={TYPES.PRIMARY}
      inverted
      scrollToTop
      onClick={(e) => handleTileV2OnClick(e, props, true)}
      url={url}
    >
      {staticText.get('continueCourse')}
      <ArrowRightIcon />
    </Button>
    : <Button
      type={TYPES.PRIMARY}
      scrollToTop
      onClick={(e) => handleTileV2OnClick(e, props, true)}
      url={url}
    >
      {staticText.get('startCourse')}
      <ArrowRightIcon />
    </Button>
}

/**
 * The series metadata
 * @param {Object} props - The props
 * @param {String} props.contentType - The content type
 * @param {Map} props.data - The series data
 * @param {Map} props.staticText - The static text
 * @param {Map} props.userNodeInfo - The userNodeInfo for the series, including featured episode
 * @param {Boolean} props.legacyData - Whether or not the data source is coming from a
 *  legacy source (i.e., non member home tile requests)
 * @returns {JSX.Element} - The series metadata
 */
function SeriesMetadata (props) {
  const {
    contentType,
    contentSubtype,
    data,
    userNodeInfo,
    legacyData,
  } = props
  const title = data.get('title', '')
  const url = getTileUrl(props)
  let featuredEpisode

  if (legacyData) {
    featuredEpisode = Map.isMap(data.get('featuredEpisode')) ? data.get('featuredEpisode', Map()) : Map()
  } else {
    featuredEpisode = Map.isMap(userNodeInfo.get('featuredEpisode')) ? userNodeInfo.get('featuredEpisode', Map()) : Map()
  }

  const featuredEpisodeUserNode = Map.isMap(featuredEpisode.get('userNode')) ? featuredEpisode.get('userNode', Map()) : Map()
  const featuredEpisodeInProgress = featuredEpisodeUserNode.get('featurePosition', 0) > 0
  const featuredEpisodeNumber = featuredEpisode.getIn(['data', 'episode'], 0)
  const featuredEpisodeSeasonNumber = featuredEpisode.getIn(['data', 'season'], 0)

  const isGuide = isGuideType(data)
  const isYogaFitnessMeditation = isSubtypeYogaFitnessMeditation({ contentSubtype })

  return (
    <>
      <MetadataHeader
        {...props}
        title={title}
        url={url}
        contentType={contentType}
        featuredEpisode={featuredEpisode}
        featuredEpisodeInProgress={featuredEpisodeInProgress}
      />
      <TileV2Guests
        {...props}
        useGraphQL={false}
        featuredEpisode={featuredEpisode}
        featuredEpisodeInProgress={featuredEpisodeInProgress}
      />
      <SeriesMiddleMetadata
        {...props}
        title={title}
        url={url}
        featuredEpisodeInProgress={featuredEpisodeInProgress}
        featuredEpisodeNumber={featuredEpisodeNumber}
        featuredEpisodeSeasonNumber={featuredEpisodeSeasonNumber}
        isGuide={isGuide}
        isYogaFitnessMeditation={isYogaFitnessMeditation}
      />
      <MetadataFooter>
        <AudioSubtitleIcons {...props} />
        <VoteReadOnly
          {...props}
          featuredEpisode={featuredEpisode}
          featuredEpisodeInProgress={featuredEpisodeInProgress}
        />
      </MetadataFooter>
      <SeriesControls {...props} />
    </>
  )
}

/**
 * The metadata for videos and episodes
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @param {Map} props.data - The content data
 * @returns {JSX.Element} - The video or episode metadata
 */
function VideoEpisodeMetadata (props) {
  const {
    contentType,
    contentSubtype,
    data,
    userLanguage,
    staticText,
  } = props
  const title = data.get('title', '')
  const seasonNumber = data.get('season', 0)
  const episodeNumber = data.get('episode', 0)
  const seriesTitle = data.get('seriesTitle', '')
  const seriesUrl = data.get('seriesPath', '')
  const isGuide = isGuideType(data)
  const isContentEpisode = isEpisode(props)
  const isYogaFitnessMeditation = isSubtypeYogaFitnessMeditation({ contentSubtype })
  let url = getTileUrl(props)

  if (contentType === TYPE_CONTENT_VIDEO) {
    const primaryCourse = getPrimaryCourse({ data, userLanguage })
    url = primaryCourse ? primaryCourse.get('path', '') : url
  }

  return (
    <>
      <MetadataHeader
        {...props}
        title={title}
        url={url}
        contentType={contentType}
      />
      <TileV2Guests {...props} useGraphQL={false} />
      <VideoEpisodeMiddleMetadata
        {...props}
        title={seriesTitle}
        url={seriesUrl}
        seasonNumber={seasonNumber}
        episodeNumber={episodeNumber}
        isGuide={isGuide}
        isContentEpisode={isContentEpisode}
        isYogaFitnessMeditation={isYogaFitnessMeditation}
        staticText={staticText}
      />
      <MetadataFooter>
        <AudioSubtitleIcons {...props} />
        <VoteReadOnly {...props} />
      </MetadataFooter>
      <VideoEpisodeControls {...props} />
    </>
  )
}

function CourseMetadata (props) {
  const { data } = props
  const title = data.get('title', '')
  const url = getTileUrl(props)

  return (
    <>
      <div>
        <TitleLink
          title={title}
          url={url}
          onClick={(e) => handleTileV2OnClick(e, props, false, 'Tile Title')}
          contentType={TYPE_CONTENT_COURSE}
        />
        <TileV2Tags {...props} />
      </div>
      <CourseControls {...props} />
    </>
  )
}

/**
 * The metadata for product content
 * @param {Object} props - The props
 * @param {Map} props.data - The product data
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} - The product metadata
 */
function ProductMetadata (props) {
  const {
    data,
    staticText,
  } = props

  const title = data.get('title', '')
  const shortDescription = data.get('shortDescription', '')
  const ctaProductUrl = data.get('ctaProductUrl') || data.get('ctaPath') || getTileUrl(props) || ''
  const ctaLabel = data.get('ctaLabel') || staticText.get('learnMore')

  return (
    <>
      <TitleLink
        title={title}
        url={ctaProductUrl}
        onClick={(e) => handleTileV2OnClick(e, props, false, 'Tile Title')}
        contentType={TYPE_CONTENT_PRODUCT}
      />
      {
        shortDescription && (
          <p className="tile-v2__description">
            {shortDescription}
          </p>
        )
      }
      <div className="tile-v2__cta-link-container">
        <Link
          to={ctaProductUrl}
          className="tile-v2__cta-link"
          onClick={(e) => handleTileV2OnClick(e, props, false, 'Tile Button')}
          target={TARGET_BLANK}
          directLink={true}
        >
          {ctaLabel}
        </Link>
        »
      </div>
    </>
  )
}

/**
 * A wrapper for the metadata
 * @param {Object} props - The props
 * @returns {JSX.Element} - The wrapped metadata children
 */
function MetadataWrapper (props) {
  const {
    children,
    hovered,
    display,
    contentType,
    showStack,
  } = props

  return (
    <div className={
      getTileV2ElementClassName({
        hovered,
        contentType,
        display,
        baseClassName: 'tile-v2__metadata',
        showStack,
      })}
    >
      {children}
    </div>
  )
}

/**
 * Renders metadata based on the content type.
 * @param {Object} props - The properties object.
 * @param {String} props.contentType - The type of content.
 * @param {Map} props.data - The content data
 * @returns {JSX.Element|null} - The rendered metadata or null.
*/
function Metadata (props) {
  const {
    contentType,
    data,
    showStack,
  } = props
  const title = data.get('title', '')
  const url = getTileUrl(props)

  if (showStack && contentType === TYPE_CONTENT_COURSE) {
    return (
      <MetadataWrapper {...props}>
        <CourseMetadata {...props} />
      </MetadataWrapper>
    )
  }

  switch (contentType) {
    case TYPE_CONTENT_SERIES:
    case TYPE_CONTENT_SERIES_GUIDE:
    case TYPE_CONTENT_COURSE:
      return (
        <MetadataWrapper {...props}>
          <SeriesMetadata {...props} />
        </MetadataWrapper>
      )
    case TYPE_CONTENT_VIDEO:
    case TYPE_CONTENT_EPISODE_GUIDE:
      return (
        <MetadataWrapper {...props}>
          <VideoEpisodeMetadata {...props} />
        </MetadataWrapper>
      )
    case TYPE_CONTENT_PERSON:
      return (
        <TitleLink
          title={title}
          url={url}
          onClick={(e) => handleTileV2OnClick(e, props)}
          contentType={contentType}
        />
      )
    case TYPE_CONTENT_TAG:
    case TYPE_CONTENT_ARTICLE:
      return (
        <MetadataWrapper {...props}>
          <TitleLink
            title={title}
            url={url}
            onClick={(e) => handleTileV2OnClick(e, props)}
            contentType={contentType}
          />
        </MetadataWrapper>
      )
    case TYPE_CONTENT_PRODUCT:
      return (
        <MetadataWrapper {...props}>
          <ProductMetadata {...props} />
        </MetadataWrapper>
      )
    default:
      return null
  }
}

/**
 * The complete rendered metadata for a tile
 * @param {Object} props - The props
 * @returns {JSX.Element} - The metadata for a content tile
 */
function TileV2InfoMetadata (props) {
  return (
    <Metadata {...props} />
  )
}

function tileV2InfoMetadataPropsAreEqual (prevProps, nextProps) {
  const prevData = prevProps.data
  const nextData = nextProps.data
  const prevUserNodeInfo = prevProps.userNodeInfo
  const nextUserNodeInfo = nextProps.userNodeInfo

  return prevData.equals(nextData) && prevUserNodeInfo.equals(nextUserNodeInfo)
}

const MemoizedTileV2InfoMetadata = React.memo(TileV2InfoMetadata, tileV2InfoMetadataPropsAreEqual)

export default MemoizedTileV2InfoMetadata
